import React from 'react';
import { motion } from 'framer-motion';
import { Search, Grid, List, FileText, Image as ImageIcon, Video, File, X } from 'lucide-react';
import { Button, Input, Card, CardBody, Chip, Spinner } from '@heroui/react';
import { FileMetadata } from '@/services/apis/files';
import { cn } from '../../utils/cn';
import MediaLibrarySkeleton from './MediaLibrarySkeleton';

export interface MediaLibraryProps {
  files: FileMetadata[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasNextPage: boolean;
  onFileSelect?: (file: FileMetadata) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  loaderRef?: React.RefObject<HTMLElement> | ((node: HTMLElement | null) => void);
  selectedFileId?: string;
}

const MediaLibrary: React.FC<MediaLibraryProps> = ({
  files,
  isLoading,
  isLoadingMore,
  hasNextPage,
  onFileSelect,
  searchQuery,
  onSearchChange,
  viewMode,
  onViewModeChange,
  loaderRef,
  selectedFileId,
}) => {
  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) return ImageIcon;
    if (type.startsWith('video/')) return Video;
    if (type === 'application/pdf') return FileText;
    return File;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getThumbnailUrl = (file: FileMetadata): string | null => {
    if (file.type.startsWith('image/')) {
      return file.url;
    }
    return null;
  };

  const renderGridView = () => (
    <div className="flex flex-wrap gap-2">
      {files.map((file) => {
        const Icon = getFileIcon(file.type);
        const thumbnailUrl = getThumbnailUrl(file);

        return (
          <motion.div
            key={file.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <Card
              isPressable
              onPress={() => onFileSelect?.(file)}
              className={cn(
                'cursor-pointer hover:shadow-lg transition-shadow max-w-49 h-50 min-w-[194px]',
                'border transition-colors',
                selectedFileId === file.id
                  ? 'border-blue-500 dark:border-blue-400 border-2'
                  : 'border-gray-200 dark:border-gray-700',
              )}
            >
              <CardBody className="p-0">
                <div className="aspect-square bg-gray-100 dark:bg-gray-800 relative overflow-hidden">
                  {thumbnailUrl ? (
                    <img src={thumbnailUrl} alt={file.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                    </div>
                  )}
                </div>
                <div className="p-3 space-y-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate" title={file.name}>
                    {file.name}
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</p>
                    <Chip size="sm" variant="flat" className="text-xs">
                      {file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                    </Chip>
                  </div>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );

  const renderListView = () => (
    <div className="space-y-2">
      {files.map((file) => {
        const Icon = getFileIcon(file.type);
        const thumbnailUrl = getThumbnailUrl(file);

        return (
          <motion.div
            key={file.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Card
              isPressable
              onPress={() => onFileSelect?.(file)}
              className={cn(
                'cursor-pointer hover:shadow-md transition-shadow w-full',
                'border transition-colors',
                selectedFileId === file.id
                  ? 'border-blue-500 dark:border-blue-400 border-2'
                  : 'border-gray-200 dark:border-gray-700',
              )}
            >
              <CardBody>
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {thumbnailUrl ? (
                      <img src={thumbnailUrl} alt={file.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <Icon className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">{file.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-sm text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</p>
                      <span className="text-gray-400 dark:text-gray-500">•</span>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{formatDate(file.createdAt)}</p>
                    </div>
                  </div>

                  <Chip size="sm" variant="flat" className="flex-shrink-0">
                    {file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                  </Chip>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          placeholder="Search files by name..."
          value={searchQuery}
          onValueChange={onSearchChange}
          startContent={<Search className="w-4 h-4 text-gray-400" />}
          endContent={
            searchQuery ? (
              <button
                onClick={() => onSearchChange('')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null
          }
          className="flex-1"
          classNames={{
            input: 'text-sm',
            inputWrapper: 'border-gray-300 dark:border-gray-600',
          }}
        />

        <div className="flex items-center gap-2">
          <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            <Button
              isIconOnly
              variant={viewMode === 'grid' ? 'solid' : 'light'}
              onPress={() => onViewModeChange('grid')}
              aria-label="Grid view"
              className={cn(
                'rounded-none border-0',
                viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'bg-transparent',
              )}
            >
              <Grid className="w-4 h-4" />
            </Button>
            <Button
              isIconOnly
              variant={viewMode === 'list' ? 'solid' : 'light'}
              onPress={() => onViewModeChange('list')}
              aria-label="List view"
              className={cn(
                'rounded-none border-0 border-l border-gray-300 dark:border-gray-600',
                viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'bg-transparent',
              )}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      {isLoading ? (
        <MediaLibrarySkeleton viewMode={viewMode} items={viewMode === 'grid' ? 12 : 8} />
      ) : files.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <File className="w-8 h-8 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No files found</h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery ? 'Try adjusting your search query' : 'Upload some files to get started'}
          </p>
        </div>
      ) : (
        <>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {files.length} file{files.length !== 1 ? 's' : ''} found
            </p>
            {viewMode === 'grid' ? renderGridView() : renderListView()}
          </div>

          {/* Infinite Scroll Trigger */}
          {hasNextPage && (
            <div ref={loaderRef as React.RefObject<HTMLDivElement>} className="flex justify-center py-4">
              {isLoadingMore && <Spinner size="lg" />}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MediaLibrary;
